import React, { useCallback, useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import Box from "@mui/material/Box";
import { Tabs, Tab, Typography, Button, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { v4 as uuidv4 } from "uuid"; // for uuid version 4
import { getListOfAccts, postAwsAcc, getAwsAcc } from "../../store/actions";
import {
  getReleaseDetailsRequest,
  clearAllReleaseMessages,
} from "../../store/actions";
import { SnackbarProvider, enqueueSnackbar } from "notistack";
import { clearAllScanServiceMessages } from "../../store/scanService/scanServiceSlice";
import AdditionalAddons from "./AddonsManagement/AdditionalAddons/AdditionalAddons";
import DocumentIcon from "@mui/icons-material/Description";
import OutboundIcon from "@mui/icons-material/Outbound";
import { EDIT_ACCOUNT_TOOLTIP_SLOT_PROPS } from "./editAccountTooltipStyles";

const PAIRING_DESCRIPTION =
  "Manage IAM role and external ID configuration for secure cloud account access.";

const RESOURCE_EXPLORER_URL =
  "https://resource-explorer.console.aws.amazon.com/resource-explorer/home?region=us-east-1#/home";

const UpdateRoleInfoComp = (props) => {
  const {
    handleUpdateIamRole,
    newRoleName,
    setNewRoleName,
    newExternalId,
    setNewExternalId,
  } = props;
  console.log("UpdateRoleInfoComp, props", props.newExternalId);
  const [isCopied, setIsCopied] = React.useState(false);

  const linkTextAreaRef = useRef(null);
  const [showCopyLinkHelp, setShowCopyLinkHelp] = useState(false);

  const getPairingConsoleUrl = () =>
    `https://console.aws.amazon.com/iam/home#/roles$new?step=review&roleType=crossAccount&accountID=${props.aws}&policies=arn%3Aaws%3Aiam%3A%3Aaws%3Apolicy%2FReadOnlyAccess,arn%3Aaws%3Aiam%3A%3Aaws%3Apolicy%2FWellArchitectedConsoleFullAccess&roleName=${newRoleName}&externalId=${newExternalId}&trustedEntityType=AWS_ACCOUNT&isThirdParty=true&awsAccount=${props.aws}`;

  const handleManualPairingGuide = async (e) => {
    e.stopPropagation();
    props.getReleaseDetails(
      encodeURIComponent(
        "configurations/Self-Onboarding-Manual-Guide-to-Pair-New-Account.pdf"
      )
    );
  };

  const handleResourceExplorerGuide = async (e) => {
    e.stopPropagation();
    props.getReleaseDetails(
      process.env.REACT_APP_RESOURCE_EXPLORER_MANUAL_GUIDE
    );
  };

  const handleClicktoCopy = () => {
    const url = getPairingConsoleUrl();
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(url);
    }
  };

  useEffect(() => {
    if (props.aws === null) {
      props.getAwsAcc();
    }
  }, []);

  const clearAllReleaseMessages = useCallback(() =>
    props.clearAllReleaseMessages()
  );
  const releaseDetails = props.releaseDetails;
  useEffect(() => {
    if (releaseDetails !== null) {
      const newTab = window.open(releaseDetails, "_blank");
      newTab.focus();
      clearAllReleaseMessages();
    }
  }, [clearAllReleaseMessages, releaseDetails]);

  const handleRefresh = () => {
    const newUuid = uuidv4();
    setNewExternalId(newUuid);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(newExternalId);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  };

  const isInvalid = () => {
    const isInvalidItems = [
      {
        field: "newRoleName",
        isInvalid:
          newRoleName === "" ||
          newRoleName === null ||
          newRoleName === undefined
            ? true
            : false,
      },
      {
        field: "newExternalId",
        isInvalid:
          newExternalId === "" ||
          newExternalId === null ||
          newExternalId === undefined
            ? true
            : false,
      },
    ];
    const resp = isInvalidItems.filter((item) => item.isInvalid);
    return resp.length > 0;
  };

  const accountStatusLabel =
    props?.selectedAccount?.status === "ACTIVE" ? "Active" : "Inactive";

  const actionLinkSx = {
    textTransform: "none",
    color: "#2563eb",
    border: "none",
    backgroundColor: "transparent",
    fontWeight: 500,
    fontSize: "0.875rem",
    borderRadius: "6px",
    px: 0.5,
    py: 0.5,
    boxShadow: "none",
    justifyContent: "flex-start",
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
      border: "none",
    },
  };

  const guideBtnSx = {
    textTransform: "none",
    color: "#2563eb",
    border: "1px solid #93c5fd",
    backgroundColor: "#fff",
    fontWeight: 500,
    fontSize: "0.8125rem",
    borderRadius: "999px",
    px: 1.5,
    py: 0.25,
    boxShadow: "none",
    minHeight: 32,
    "&:hover": {
      backgroundColor: "#eff6ff",
      border: "1px solid #93c5fd",
    },
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          p: 2.5,
          backgroundColor: "#f3f4f6",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            pb: 1.25,
            mb: 1.5,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography
            sx={{ fontWeight: 700, color: "#111827", fontSize: "1.125rem" }}
          >
            Update Pairing
          </Typography>
          <Typography
            sx={{ fontSize: "0.8125rem", fontWeight: 700, color: "#111827" }}
          >
            [Status: {accountStatusLabel}]
          </Typography>
        </Box>

        <Typography
          sx={{
            color: "#334155",
            fontSize: "0.875rem",
            lineHeight: 1.5,
            mb: 2.5,
          }}
        >
          {PAIRING_DESCRIPTION}
        </Typography>

        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#94a3b8",
            mb: 1.25,
          }}
        >
          Setup Actions
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr max-content",
            columnGap: 2,
            rowGap: 1.25,
            alignItems: "center",
            mb: 2.5,
          }}
        >
          <Button
            variant="text"
            sx={actionLinkSx}
            disabled={!props.aws || !newRoleName || !newExternalId}
            endIcon={<OutboundIcon sx={{ fontSize: 16 }} />}
            onClick={() => window.open(getPairingConsoleUrl(), "_blank")}
          >
            1 Account Pairing
          </Button>
          <Button
            variant="outlined"
            sx={guideBtnSx}
            disabled={props.isReleaseDetailsGetting}
            startIcon={<DocumentIcon sx={{ fontSize: 16 }} />}
            onClick={handleManualPairingGuide}
          >
            Guide
          </Button>

          <Button
            variant="text"
            sx={actionLinkSx}
            endIcon={<OutboundIcon sx={{ fontSize: 16 }} />}
            onClick={() => window.open(RESOURCE_EXPLORER_URL, "_blank")}
          >
            2 Enable Resource Explorer
          </Button>
          <Button
            variant="outlined"
            sx={guideBtnSx}
            disabled={props.isReleaseDetailsGetting}
            startIcon={<DocumentIcon sx={{ fontSize: 16 }} />}
            onClick={handleResourceExplorerGuide}
          >
            Guide
          </Button>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#64748b",
              mb: 0.75,
            }}
          >
            IAM Role
          </Typography>
          <TextField
            fullWidth
            size="small"
            name="newRoleName"
            value={newRoleName}
            placeholder="Role Name"
            type="text"
            onChange={(e) => setNewRoleName(e.target.value)}
            error={!newRoleName}
            helperText={!newRoleName && "IAM Role is required"}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                borderRadius: "8px",
              },
            }}
          />
        </Box>

        <Box sx={{ mb: 1 }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#64748b",
              mb: 0.75,
            }}
          >
            External ID
          </Typography>
          <TextField
            fullWidth
            size="small"
            name="newExternalId"
            value={newExternalId}
            placeholder="External ID"
            type="text"
            disabled={true}
            error={!newExternalId}
            helperText={!newExternalId && "External Id is required"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopy} size="small">
                    {isCopied ? (
                      <Typography sx={{ fontSize: "0.75rem", color: "#2563eb" }}>
                        Copied
                      </Typography>
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 18, color: "#64748b" }} />
                    )}
                  </IconButton>
                  <IconButton onClick={handleRefresh} size="small">
                    <RefreshIcon sx={{ fontSize: 18, color: "#64748b" }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                borderRadius: "8px",
              },
            }}
          />
        </Box>

        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="text"
            sx={{ ...actionLinkSx, fontSize: "0.8125rem" }}
            onClick={handleClicktoCopy}
          >
            Copy Pairing Link
          </Button>
          <Tooltip
            title="Please make sure you are logged in the AWS account you are onboarding, prior to completing this step.
                              Open a new tab in your browser. Copy the link above and paste it in the URL bar of this new tab.
                              Complete setting up a role through AWS IAM role creation console, make sure that the role's name is set as:
                              CloudCatcherTrustRole"
            slotProps={EDIT_ACCOUNT_TOOLTIP_SLOT_PROPS}
            onClose={() => setShowCopyLinkHelp(false)}
            open={showCopyLinkHelp}
            placement="top"
            arrow
            disableHoverListener
          >
            <Button
              variant="text"
              sx={{ ...actionLinkSx, fontSize: "0.8125rem", minWidth: 0 }}
              onClick={() => setShowCopyLinkHelp(!showCopyLinkHelp)}
            >
              Help
            </Button>
          </Tooltip>
          {/* Keep ref available for any legacy consumers */}
          <textarea
            readOnly
            ref={linkTextAreaRef}
            value={getPairingConsoleUrl()}
            style={{ display: "none" }}
            aria-hidden
          />
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          onClick={props.closeModal}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            px: 2.5,
            color: "#334155",
            borderColor: "#cbd5e1",
            backgroundColor: "#fff",
            "&:hover": {
              borderColor: "#94a3b8",
              backgroundColor: "#f8fafc",
            },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          disabled={
            props.awsAccountPosting ||
            isInvalid() ||
            props.accountStatusLoading ||
            props.accountStatusError
          }
          onClick={handleUpdateIamRole}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            borderRadius: "8px",
            px: 3,
            py: 1,
            backgroundColor: "#111827",
            color: "#fff",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#0f172a",
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              backgroundColor: "#cbd5e1",
              color: "#fff",
            },
          }}
        >
          {props?.selectedAccount?.status === "ACTIVE"
            ? "Update Role and External ID"
            : "Activate"}
        </Button>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => {
  const { awsAccountPosting, postAwsAccMessage, postAwsAccError } =
    state.awsAcc;

  const { aws } = state.awsAcc;

  const {
    isReleaseDetailsGetting,
    getReleaseDetailsMessage,
    getReleaseDetailsError,
    releaseDetails,
  } = state.releaseNotes;

  const { accountStatusLoading, accountStatusResult, accountStatusError } =
    state.scanService;

  return {
    awsAccountPosting,
    postAwsAccMessage,
    postAwsAccError,

    isReleaseDetailsGetting,
    getReleaseDetailsMessage,
    getReleaseDetailsError,
    releaseDetails,

    aws,

    accountStatusLoading,
    accountStatusResult,
    accountStatusError,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    postAwsAcc: (awsAccountUpdateRequest) => {
      dispatch(postAwsAcc(awsAccountUpdateRequest));
    },
    getListOfAccts: () => {
      dispatch(getListOfAccts());
    },
    getReleaseDetails: (fileName) => {
      dispatch(getReleaseDetailsRequest(fileName));
    },
    clearAllReleaseMessages: () => {
      dispatch(clearAllReleaseMessages());
    },
    getAwsAcc: () => {
      dispatch(getAwsAcc());
    },
    clearAllScanServiceMessages: () => {
      dispatch(clearAllScanServiceMessages());
    },
  };
};

const UpdateRoleInfo = connect(
  mapStateToProps,
  mapDispatchToProps
)(UpdateRoleInfoComp);

const UpdateIamRole = (props) => {
  console.log("UpdateIamRole, props", props);
  console.log("UpdateIamRole, props.selectedAccount", props.selectedAccount);
  console.log("UpdateIamRole, props.externalId", props.externalId);
  const [tabValue, setTabValue] = useState(0);
  const [updateRoleDone, setUpdateRoleDone] = useState(false);
  const [newRoleName, setNewRoleName] = useState(
    props.selectedAccount?.iamrole || "CloudcatcherAuditTrustRole"
  );
  const [newExternalId, setNewExternalId] = useState(props?.externalId);
  const [accountStatus, setAccountStatus] = useState(
    props.selectedAccount?.status
  );

  const handleUpdateIamRole = () => {
    const updateRoleRequest = {
      accountNumber: props.selectedAccount.accountNumber,
      iamRole: newRoleName,
      externalId: newExternalId,
    };
    console.log("updateRoleRequest: ", updateRoleRequest);
    props.postAwsAcc(updateRoleRequest);
  };

  const accountStatusResult = props.accountStatusResult;
  const accountStatusError = props.accountStatusError;
  useEffect(() => {
    if (accountStatusResult) {
      const updateRoleRequest = {
        accountNumber: props.selectedAccount.accountNumber,
        iamRole: newRoleName,
        externalId: newExternalId,
      };
      console.log("updateRoleRequest: ", updateRoleRequest);
      props.postAwsAcc(updateRoleRequest);
    }
    if (accountStatusError) {
      enqueueSnackbar(accountStatusError, { variant: "error" });
    }
    // props.clearAllScanServiceMessages();
  }, [accountStatusResult, accountStatusError]);

  const postAwsAccMessage = props.postAwsAccMessage;
  const getListOfAccts = useCallback(() => {
    props.getListOfAccts();
  }, []);
  const closeModal = useCallback(() => {
    // clear scan service messages
    props.clearAllScanServiceMessages();
    props.closeModal();
  }, []);
  useEffect(() => {
    console.log("UpdateIamRole: useEffect");
    if (postAwsAccMessage) {
      if (accountStatus === "INACTIVE") {
        setUpdateRoleDone(true);
        setTabValue(1);
        setAccountStatus("ACTIVATED");
      } else if (accountStatus === "ACTIVE") {
        closeModal();
      }
      if (accountStatus !== "ACTIVATED") {
        getListOfAccts();
      }
    }
  }, [
    postAwsAccMessage,
    getListOfAccts,
    closeModal,
    newExternalId,
    newRoleName,
    accountStatus,
  ]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <SnackbarProvider />
      {/* {props?.selectedAccount?.status === "ACTIVE" ? ( */}
      {accountStatus === "ACTIVE" ? (
        <UpdateRoleInfo
          handleUpdateIamRole={handleUpdateIamRole}
          selectedAccount={props.selectedAccount}
          newRoleName={newRoleName}
          setNewRoleName={setNewRoleName}
          newExternalId={newExternalId}
          setNewExternalId={setNewExternalId}
          closeModal={closeModal}
        />
      ) : (
        <div>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="IAM Role Update" />
            <Tab label="Addons" disabled={!updateRoleDone} />
          </Tabs>
          {tabValue === 0 && (
            <UpdateRoleInfo
              handleUpdateIamRole={handleUpdateIamRole}
              selectedAccount={props.selectedAccount}
              newRoleName={newRoleName}
              setNewRoleName={setNewRoleName}
              newExternalId={newExternalId}
              setNewExternalId={setNewExternalId}
              closeModal={closeModal}
            />
          )}
          {tabValue === 1 && (
            <div
              style={{
                height: 400,
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <AdditionalAddons
                addOns={props.addOns}
                editAccount={props.selectedAccount}
              />
              <footer
                style={{
                  marginTop: "auto",
                  padding: "1rem",
                  display: "flex",
                  justifyContent: "flex-end",
                  borderTop: "1px solid #ddd",
                }}
              >
                <button
                  style={{
                    padding: "0.5rem 1rem",
                    backgroundColor: "#6c757d",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                  onClick={closeModal} // Replace with your close modal function
                >
                  Close
                </button>
              </footer>
            </div>
          )}
        </div>
      )}
    </Box>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(UpdateIamRole);
